import { useCallback } from 'react'
import { useQueryClient } from 'react-query'
import Styles from './AdjustMediaModal.module.scss'
import { Modal, StackModalProps } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Trash2 } from 'blaze-ui/icons'
import { useInfiniteBrandKitMediaFiles, IMediaFile } from 'hooks/queries/brandKits/useInfiniteBrandKitMediaFiles'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { MediaFileClient } from 'api/clients/MediaFileClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { collectResults } from 'utils/query'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'

type Props = StackModalProps & {
  brandKitId: number
}

export const AdjustMediaModal = ({ brandKitId, close }: Props) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  const mediaQuery = useInfiniteBrandKitMediaFiles({
    brandKitId,
    items: 20,
    media_source: 'website',
    order_by: 'recent',
  })
  const mediaFiles = collectResults(mediaQuery, (data) => data.media_files)
  const { hasNextPage, fetchNextPage } = mediaQuery

  const handleDelete = useCallback(
    async (mf: IMediaFile) => {
      const response = await MediaFileClient.destroy(workspaceId, mf.media_file_id)
      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        showErrorMessageToast('Failed to delete media file.')
        return
      }
      await queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits', brandKitId, 'mediaFiles'])
    },
    [workspaceId, brandKitId, queryClient],
  )

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    if (hasNextPage && el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
      fetchNextPage()
    }
  }

  return (
    <Modal.Root size="lg" onClose={close}>
      <Modal.Content compact={false} className={Styles.contentWrapper}>
        <div className={Styles.body}>
          <div className={Styles.headerContent}>
            <h2 className={Styles.title}>Media Blaze pulled from your website</h2>
            <p className={Styles.subtitle}>
              Remove anything that doesn&apos;t represent your brand, or upload additional assets you want used in your
              content.
            </p>
          </div>
          <div className={Styles.gridScroll} onScroll={handleScroll}>
            <div className={Styles.grid}>
              {mediaQuery.isLoading
                ? Array.from({ length: 10 }).map((_, i) => <div key={i} className={Styles.skeletonTile} />)
                : mediaFiles.map((mf: IMediaFile) => (
                    <div key={mf.media_file_id} className={Styles.tile}>
                      {isVideoMime(mf.mime_type) ? (
                        <video src={mf.source_url} className={Styles.tileMedia} muted />
                      ) : (
                        <img
                          src={mf.thumbnail_url || mf.source_url}
                          alt=""
                          className={Styles.tileMedia}
                          loading="eager"
                          decoding="async"
                        />
                      )}
                      {mf.duration != null && mf.duration > 0 && (
                        <span className={Styles.videoBadge}>{formatDuration(mf.duration)}</span>
                      )}
                      <div className={Styles.tileTrash}>
                        <IconButton
                          icon={Trash2}
                          variant="secondary"
                          size="xs"
                          onPress={() => handleDelete(mf)}
                          aria-label="Delete"
                        />
                      </div>
                    </div>
                  ))}
            </div>
          </div>
        </div>
      </Modal.Content>

      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button variant="tertiary" onPress={close}>
            Cancel
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent>
          <Button variant="primary" onPress={close}>
            Save
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}

const isVideoMime = (mime?: string) => !!mime && mime.startsWith('video/')

const formatDuration = (seconds: number) => {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}
